/* @jsx h */
import { computed, defineComponent, h, onMounted, ref } from "vue";
import { useLocalization } from "@lemmonhq/uikit";
import { useTrpc } from "@app/vue/providers/TrpcProvider";
import { useCustomer } from "../../providers/auth-context";
import type {
  CoinWalletPass,
  MembershipWalletPass,
} from "@app/server/trpc/routers/wallet-passes";
import { WalletBadges } from "../CollectedPage/WalletBadges";
import { passPlatformEnabled } from "../CollectedPage/passDesign";

type InstallablePass = MembershipWalletPass | CoinWalletPass;

export const WalletPassesPage = defineComponent({
  name: "WalletPassesPage",
  setup() {
    const { t } = useLocalization();
    const trpc = useTrpc();
    const { customer } = useCustomer();

    const passes = ref<InstallablePass[]>([]);
    const loading = ref(true);
    const error = ref(false);

    const isRegistered = computed(
      () =>
        !!customer.value && !("anon" in customer.value && customer.value.anon),
    );

    onMounted(async () => {
      if (!isRegistered.value) {
        loading.value = false;
        return;
      }
      try {
        const [memberships, coins] = await Promise.all([
          trpc.walletPasses.membershipPasses.query(),
          trpc.walletPasses.coinPasses.query(),
        ]);
        // Only passes whose design offers at least one wallet are installable.
        passes.value = [...memberships, ...coins].filter(
          (pass) =>
            passPlatformEnabled(pass.kind, pass.design, "apple") ||
            passPlatformEnabled(pass.kind, pass.design, "google"),
        );
      } catch (e) {
        error.value = true;
      } finally {
        loading.value = false;
      }
    });

    const card = (pass: InstallablePass) => (
      <div
        key={pass.program_id}
        class="rounded-2xl border border-base-300 bg-base-100 p-5 shadow-sm"
      >
        <div class="flex items-center gap-3">
          <div class="flex h-11 w-11 items-center justify-center rounded-xl bg-primary/10 text-primary">
            <span class="material-symbols-outlined">
              {pass.kind === "coin" ? pass.icon || "toll" : "card_membership"}
            </span>
          </div>
          <div class="min-w-0 flex-1">
            <div class="truncate text-lg font-semibold text-accent">
              {pass.program_name}
            </div>
            <div class="text-xs text-base-content/50">
              {pass.kind === "coin"
                ? pass.coin_label
                : pass.expires_at
                  ? t("customer.wallet.expires_on", [
                      new Date(pass.expires_at).toLocaleDateString(),
                    ])
                  : t("customer.wallet.no_expiry")}
            </div>
          </div>
        </div>
        <div class="mt-4">
          <WalletBadges
            appleUrl={
              !passPlatformEnabled(pass.kind, pass.design, "apple")
                ? null
                : `/api/v3/customer/wallet/pass/${pass.serial}.pkpass`
            }
            googleUrl={
              !passPlatformEnabled(pass.kind, pass.design, "google")
                ? null
                : (pass.google_save_url ??
                  `/api/v3/customer/wallet/google/${pass.serial}`)
            }
          />
        </div>
      </div>
    );

    return () => (
      <div class="mx-auto min-h-screen w-full max-w-md space-y-5 bg-base-200 px-4 py-8">
        <div>
          <h1 class="text-2xl font-bold text-accent">
            {t("customer.wallet.title")}
          </h1>
          <p class="mt-1 text-sm text-base-content/60">
            {t("customer.wallet.subtitle")}
          </p>
        </div>

        {loading.value ? (
          <div class="py-16 text-center">
            <span class="loading loading-spinner loading-lg text-primary" />
          </div>
        ) : !isRegistered.value ? (
          <div class="rounded-2xl border border-base-300 bg-base-100 p-6 text-center text-sm text-base-content/60">
            {t("customer.wallet.sign_in_required")}
          </div>
        ) : error.value ? (
          <div class="alert alert-error text-sm">
            {t("customer.wallet.load_error")}
          </div>
        ) : passes.value.length === 0 ? (
          <div class="rounded-2xl border border-dashed border-base-300 bg-base-100 p-8 text-center text-sm text-base-content/50">
            {t("customer.wallet.empty")}
          </div>
        ) : (
          <div class="space-y-4">{passes.value.map(card)}</div>
        )}
      </div>
    );
  },
});
